import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit, FiToggleLeft, FiToggleRight, FiEye, FiEyeOff, FiSearch, FiUsers, FiUser, FiFilter, FiGrid, FiList, FiRefreshCw } from 'react-icons/fi';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const TeamManagement = () => {

  const [activeTab, setActiveTab] = useState('officers');
  const [salesOfficers, setSalesOfficers] = useState([]);
  const [salesPersons, setSalesPersons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedOfficer, setSelectedOfficer] = useState(null);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [officerFilter, setOfficerFilter] = useState('all'); // For sales persons filtering
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOfficers, setTotalOfficers] = useState(0);
  const [totalPersons, setTotalPersons] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [debouncedSearch, setDebouncedSearch] = useState(search);

  const [officerForm, setOfficerForm] = useState({
    name: '',
    type: 'freelancer',
    password: '',
    mobileNumber: '',
    address: '',
    city: '',
    state: ''
  });

  const [personForm, setPersonForm] = useState({
    name: '',
    type: 'freelancer',
    password: '',
    mobileNumber: '',
    salesOfficer: '',
    address: '',
    city: '',
    state: ''
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    fetchSalesOfficers();
  }, []);

  useEffect(() => {
    if (activeTab === 'officers') {
      fetchSalesOfficers();
    } else {
      fetchSalesPersons();
    }
  }, [debouncedSearch, statusFilter, officerFilter, currentPage, itemsPerPage, activeTab]);

  const fetchSalesOfficers = async (isManualRefresh = false) => {
    try {
      if (isManualRefresh) {
        setRefreshing(true);
        toast.loading('Refreshing sales officers...', { id: 'refresh' });
      } else {
        setLoading(true);
      }

      const params = {
        page: currentPage,
        limit: itemsPerPage,
        search,
        status: statusFilter
      };

      console.log('Fetching sales officers with params:', params);
      const response = await api.get('/superadmin/sales-officers', { params });
      console.log('Sales officers response:', response.data);
      
      setSalesOfficers(response.data.data || []);
      setTotalPages(response.data.totalPages || 1);
      setTotalOfficers(response.data.total || 0);
      
      // Show success message for manual refresh
      if (isManualRefresh) {
        toast.success(`Refreshed! Found ${response.data.data?.length || 0} officers`, { id: 'refresh' });
      }
    } catch (error) {
      console.error('Fetch sales officers error:', error);
      toast.error(`Failed to fetch sales officers: ${error.response?.data?.message || error.message}`, { id: 'refresh' });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchSalesPersons = async (isManualRefresh = false) => {
    try {
      if (isManualRefresh) {
        setRefreshing(true);
        toast.loading('Refreshing sales persons...', { id: 'refresh' });
      } else {
        setLoading(true);
      }

      const params = {
        page: currentPage,
        limit: itemsPerPage,
        search,
        status: statusFilter
      };

      if (officerFilter !== 'all') {
        params.salesOfficer = officerFilter;
      }

      console.log('Fetching sales persons with params:', params);
      const response = await api.get('/superadmin/sales-persons', { params });
      console.log('Sales persons response:', response.data);
      
      setSalesPersons(response.data.data || []);
      setTotalPages(response.data.totalPages || 1);
      setTotalPersons(response.data.total || 0);
      
      // Show success message for manual refresh
      if (isManualRefresh) {
        toast.success(`Refreshed! Found ${response.data.data?.length || 0} persons`, { id: 'refresh' });
      }
    } catch (error) {
      console.error('Fetch sales persons error:', error);
      toast.error(`Failed to fetch sales persons: ${error.response?.data?.message || error.message}`, { id: 'refresh' });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const openOfficerModal = (officer = null) => {
    setSelectedOfficer(officer);
    if (officer) {
      setOfficerForm({
        name: officer.name,
        type: officer.type || 'freelancer',
        password: '',
        mobileNumber: officer.mobileNumber,
        address: officer.address || '',
        city: officer.city || '',
        state: officer.state || ''
      });
    } else {
      setOfficerForm({
        name: '',
        type: 'freelancer',
        password: '',
        mobileNumber: '',
        address: '',
        city: '',
        state: ''
      });
    }
    setShowModal(true);
  };

  const openPersonModal = (person = null) => {
    setSelectedPerson(person);
    if (person) {
      setPersonForm({
        name: person.name,
        type: person.type || 'freelancer',
        password: '',
        mobileNumber: person.mobileNumber,
        salesOfficer: person.salesOfficer?._id || '',
        address: person.address || '',
        city: person.city || '',
        state: person.state || ''
      });
    } else {
      setPersonForm({
        name: '',
        type: 'freelancer',
        password: '',
        mobileNumber: '',
        salesOfficer: '',
        address: '',
        city: '',
        state: ''
      });
    }
    setShowModal(true);
  };

  const resetForms = () => {
    setOfficerForm({
      name: '',
      type: 'freelancer',
      password: '',
      mobileNumber: '',
      address: '',
      city: '',
      state: ''
    });
    setPersonForm({
      name: '',
      type: 'freelancer',
      password: '',
      mobileNumber: '',
      salesOfficer: '',
      address: '',
      city: '',
      state: ''
    });
    setSelectedOfficer(null);
    setSelectedPerson(null);
  };

  const handleSubmitOfficer = async (e) => {
    e.preventDefault();
    try {
      if (selectedOfficer) {
        await api.put(`/superadmin/sales-officers/${selectedOfficer._id}`, officerForm);
        toast.success('Sales officer updated successfully');
      } else {
        await api.post('/superadmin/sales-officers', officerForm);
        toast.success('Sales officer created successfully');
      }
      setShowModal(false);
      resetForms();
      fetchSalesOfficers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    }
  };

  const handleSubmitPerson = async (e) => {
    e.preventDefault();
    try {
      if (selectedPerson) {
        await api.put(`/superadmin/sales-persons/${selectedPerson._id}`, personForm);
        toast.success('Sales person updated successfully');
      } else {
        await api.post('/superadmin/sales-persons', personForm);
        toast.success('Sales person created successfully');
      }
      setShowModal(false);
      resetForms();
      fetchSalesPersons();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    }
  };

  const toggleOfficerStatus = async (officerId) => {
    try {
      await api.patch(`/superadmin/sales-officers/${officerId}/toggle-status`);
      fetchSalesOfficers();
      toast.success('Status updated successfully');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const togglePersonStatus = async (personId) => {
    try {
      await api.patch(`/superadmin/sales-persons/${personId}/toggle-status`);
      fetchSalesPersons();
      toast.success('Status updated successfully');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      {/* Header */}
      <div className="mb-8" style={{marginTop: '70px'}}>
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Team Management</h1>
              <p className="text-gray-600">Manage sales officers and sales persons efficiently</p>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-500">Total Members</div>
              <div className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                {totalOfficers + totalPersons}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">Sales Officers</h3>
              <p className="text-4xl font-bold text-gray-900">{totalOfficers}</p>
            </div>
            <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
              <FiUsers className="text-white text-2xl" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">Sales Persons</h3>
              <p className="text-4xl font-bold text-gray-900">{totalPersons}</p>
            </div>
            <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-green-600 rounded-2xl flex items-center justify-center shadow-lg">
              <FiUser className="text-white text-2xl" />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
        {/* Tabs */}
        <div className="border-b border-gray-200">
          <div className="flex">
            <button
              className={`flex items-center gap-2 px-6 py-4 font-semibold text-sm transition-all duration-300 border-b-2 ${
                activeTab === 'officers'
                  ? 'border-purple-600 text-purple-600 bg-purple-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
              onClick={() => setActiveTab('officers')}
            >
              <FiUsers />
              Sales Officers
            </button>
            <button
              className={`flex items-center gap-2 px-6 py-4 font-semibold text-sm transition-all duration-300 border-b-2 ${
                activeTab === 'persons'
                  ? 'border-purple-600 text-purple-600 bg-purple-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
              onClick={() => setActiveTab('persons')}
            >
              <FiUser />
              Sales Persons
            </button>
          </div>
        </div>

        {/* Sales Officers Tab */}
        {activeTab === 'officers' && (
          <div className="p-6">
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <div className="flex-1 relative">
                <FiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-4 focus:ring-purple-100 transition-all duration-300 outline-none"
                  placeholder="Search by name, employee ID, or mobile..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              
              <select 
                className="px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-4 focus:ring-purple-100 transition-all duration-300 outline-none bg-white font-medium text-gray-700"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>

              <button
                onClick={() => fetchSalesOfficers(true)}
                disabled={refreshing}
                className="flex items-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold rounded-xl hover:from-blue-600 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-md hover:shadow-lg"
              >
                <FiRefreshCw className={`text-sm ${refreshing ? 'animate-spin' : ''}`} />
                <span className="text-sm">{refreshing ? 'Refreshing...' : 'Refresh'}</span>
              </button>

              <button 
                className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-xl hover:from-purple-700 hover:to-pink-700 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
                onClick={() => openOfficerModal()}
              >
                <FiPlus />
                Create Officer
              </button>
            </div>

            <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
              <table className="w-full">
                <thead className="bg-gray-100 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase">Name</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase">Type</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase">Employee ID</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase">Mobile</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase">Password</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase">City</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase">Status</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase">Actions</th>
                  </tr>
                </thead>

                <tbody className="bg-white divide-y divide-gray-100">
                  {salesOfficers.map((officer) => (
                    <tr key={officer._id} className="hover:bg-gray-50">
                      {/* Name */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-medium text-gray-900">
                          {officer.name}
                        </span>
                      </td>

                      {/* Type */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-3 py-1 text-xs font-semibold rounded-full ${
                            officer.type === "freelancer"
                              ? "bg-blue-100 text-blue-700"
                              : "bg-green-100 text-green-700"
                          }`}
                        >
                          {officer.type === "freelancer" ? "Freelancer" : "Full Time"}
                        </span>
                      </td>

                      {/* Employee ID */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <code className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-sm font-mono">
                          {officer.employeeId}
                        </code>
                      </td>

                      {/* Mobile */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {officer.mobileNumber}
                      </td>

                      {/* Password */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <code className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-sm font-mono">
                          {officer.plainTextPassword}
                        </code>
                      </td>

                      {/* City */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {officer.city || "N/A"}
                      </td>

                      {/* Status */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-3 py-1 text-xs font-semibold rounded-full ${
                            officer.isActive
                              ? "bg-green-100 text-green-700"
                              : "bg-red-100 text-red-700"
                          }`}
                        >
                          {officer.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>

                      {/* Actions */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => openOfficerModal(officer)}
                            className="p-2 bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200"
                          >
                            <FiEdit className="w-4 h-4" />
                          </button>

                          <button
                            onClick={() => toggleOfficerStatus(officer._id)}
                            className={`p-2 rounded-md ${
                              officer.isActive
                                ? "bg-orange-100 text-orange-600 hover:bg-orange-200"
                                : "bg-green-100 text-green-600 hover:bg-green-200"
                            }`}
                          >
                            {officer.isActive ? (
                              <FiToggleRight className="w-4 h-4" />
                            ) : (
                              <FiToggleLeft className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {salesOfficers.length === 0 && (
                <div className="text-center py-12 bg-gray-50">
                  <FiUsers className="mx-auto text-4xl text-gray-300 mb-3" />
                  <p className="text-gray-500 font-medium">
                    No sales officers found
                  </p>
                </div>
              )}
            </div>
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-gray-50 to-gray-100 border-t-2 border-purple-100">
                <div className="text-sm text-gray-600 font-medium">
                  Showing <span className="font-bold text-purple-600">{salesOfficers.length}</span> of <span className="font-bold text-purple-600">{totalOfficers}</span> officers
                </div>
                <div className="flex gap-2">
                  <button
                    className="px-4 py-2 bg-white border-2 border-gray-200 rounded-lg font-semibold text-gray-700 hover:border-purple-500 hover:text-purple-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-105 shadow-sm hover:shadow-md"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                  <button
                    className="px-4 py-2 bg-white border-2 border-gray-200 rounded-lg font-semibold text-gray-700 hover:border-purple-500 hover:text-purple-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-105 shadow-sm hover:shadow-md"
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Sales Persons Tab */}
        {activeTab === 'persons' && (
          <div className="p-6">
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <div className="flex-1 relative">
                <FiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-4 focus:ring-purple-100 transition-all duration-300 outline-none"
                  placeholder="Search by name, employee ID, or mobile..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              
              <select 
                className="px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-4 focus:ring-purple-100 transition-all duration-300 outline-none bg-white font-medium text-gray-700"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>

              <select 
                className="px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-4 focus:ring-purple-100 transition-all duration-300 outline-none bg-white font-medium text-gray-700"
                value={officerFilter}
                onChange={(e) => setOfficerFilter(e.target.value)}
              >
                <option value="all">All Sales Officers</option>
                {salesOfficers.map(officer => (
                  <option key={officer._id} value={officer._id}>
                    {officer.name} ({officer.employeeId})
                  </option>
                ))}
              </select>

              <button
                onClick={() => fetchSalesPersons(true)}
                disabled={refreshing}
                className="flex items-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold rounded-xl hover:from-blue-600 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-md hover:shadow-lg"
              >
                <FiRefreshCw className={`text-sm ${refreshing ? 'animate-spin' : ''}`} />
                <span className="text-sm">{refreshing ? 'Refreshing...' : 'Refresh'}</span>
              </button>

              <button 
                className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-xl hover:from-purple-700 hover:to-pink-700 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap"
                onClick={() => openPersonModal()}
              >
                <FiPlus />
                Create Person
              </button>
            </div>

            <div className="overflow-x-auto rounded-xl border-2 border-purple-100">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-purple-600 to-pink-600">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-900 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-900 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-900 uppercase tracking-wider">Employee ID</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-900 uppercase tracking-wider">Mobile Number</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-900 uppercase tracking-wider">Password</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-900 uppercase tracking-wider">Sales Officer</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-900 uppercase tracking-wider">City</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-900 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-900 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {salesPersons.map((person) => (
                    <tr key={person._id} className="hover:bg-gradient-to-r hover:from-purple-50 hover:to-pink-50 transition-all duration-300 transform hover:scale-[1.01]">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-2 h-2 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full mr-3 animate-pulse"></div>
                          <span className="text-sm font-bold text-gray-900">{person.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-3 py-1 text-xs font-bold rounded-full ${
                          person.type === 'freelancer' 
                            ? 'bg-gradient-to-r from-blue-400 to-blue-600 text-white' 
                            : 'bg-gradient-to-r from-green-400 to-green-600 text-white'
                        }`}>
                          {person.type === 'freelancer' ? 'Freelancer' : 'Permanent'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <code className="px-3 py-1 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 rounded-lg text-sm font-mono font-semibold border-2 border-gray-300 hover:from-purple-600 hover:to-pink-600 hover:text-white transition-all duration-300">
                          {person.employeeId}
                        </code>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 font-medium">{person.mobileNumber}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <code className="px-3 py-1 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 rounded-lg text-sm font-mono font-semibold border-2 border-gray-300 hover:from-purple-600 hover:to-pink-600 hover:text-white transition-all duration-300">
                          {person.plainTextPassword}
                        </code>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {person.salesOfficer ? (
                          <div>
                            <div className="text-sm font-bold text-gray-900">{person.salesOfficer.name}</div>
                            <div className="text-xs text-gray-500 font-medium">{person.salesOfficer.employeeId}</div>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400 italic">Not Assigned</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 font-medium">{person.city || 'N/A'}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-3 py-1 text-xs font-bold rounded-full ${
                          person.isActive 
                            ? 'bg-gradient-to-r from-green-400 to-green-600 text-white animate-pulse' 
                            : 'bg-gradient-to-r from-red-400 to-red-600 text-white'
                        }`}>
                          {person.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <button
                            className="p-2 bg-gradient-to-r from-purple-100 to-pink-100 text-purple-600 rounded-lg hover:from-purple-600 hover:to-pink-600 hover:text-white transform hover:scale-110 transition-all duration-300 shadow-md hover:shadow-lg"
                            onClick={() => openPersonModal(person)}
                          >
                            <FiEdit className="w-5 h-5" />
                          </button>
                          <button
                            className={`p-2 rounded-lg transform hover:scale-110 transition-all duration-300 shadow-md hover:shadow-lg ${
                              person.isActive 
                                ? 'bg-gradient-to-r from-orange-100 to-yellow-100 text-orange-600 hover:from-orange-600 hover:to-yellow-600 hover:text-white' 
                                : 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-600 hover:from-green-600 hover:to-emerald-600 hover:text-white'
                            }`}
                            onClick={() => togglePersonStatus(person._id)}
                          >
                            {person.isActive ? <FiToggleRight className="w-5 h-5" /> : <FiToggleLeft className="w-5 h-5" />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {salesPersons.length === 0 && (
                <div className="text-center py-16 bg-gradient-to-br from-purple-50 to-pink-50">
                  <FiUser className="mx-auto text-6xl text-purple-300 mb-4" />
                  <p className="text-gray-500 text-lg font-medium">No sales persons found</p>
                </div>
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-gray-50 to-gray-100 border-t-2 border-purple-100">
                <div className="text-sm text-gray-600 font-medium">
                  Showing <span className="font-bold text-purple-600">{salesPersons.length}</span> of <span className="font-bold text-purple-600">{totalPersons}</span> persons
                </div>
                <div className="flex gap-2">
                  <button
                    className="px-4 py-2 bg-white border-2 border-gray-200 rounded-lg font-semibold text-gray-700 hover:border-purple-500 hover:text-purple-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-105 shadow-sm hover:shadow-md"
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                  <button
                    className="px-4 py-2 bg-white border-2 border-gray-200 rounded-lg font-semibold text-gray-700 hover:border-purple-500 hover:text-purple-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-105 shadow-sm hover:shadow-md"
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-6 py-4 border-b bg-white flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                  {activeTab === "officers" ? (
                    <FiUsers className="text-purple-600 text-xl" />
                  ) : (
                    <FiUser className="text-purple-600 text-xl" />
                  )}
                </div>

                <div>
                  <h3 className="text-lg font-bold text-gray-900">
                    {activeTab === "officers"
                      ? selectedOfficer
                        ? "Edit Sales Officer"
                        : "Create Sales Officer"
                      : selectedPerson
                      ? "Edit Sales Person"
                      : "Create Sales Person"}
                  </h3>
                  <p className="text-xs text-gray-500">Fill the form below</p>
                </div>
              </div>

              <button
                onClick={() => setShowModal(false)}
                className="w-9 h-9 rounded-lg hover:bg-gray-100 flex items-center justify-center transition"
              >
                <span className="text-xl text-gray-600">×</span>
              </button>
            </div>

            <form
              onSubmit={
                activeTab === "officers"
                  ? handleSubmitOfficer
                  : handleSubmitPerson
              }
              className="flex flex-col flex-1"
            >
              {/* BODY */}
              <div className="p-6 overflow-y-auto flex-1 bg-gray-50 space-y-5">

                {/* ================= OFFICER FORM ================= */}
                {activeTab === "officers" ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

                    {/* Name */}
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">
                        Name *
                      </label>
                      <input
                        type="text"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                        value={officerForm.name}
                        onChange={(e) =>
                          setOfficerForm({ ...officerForm, name: e.target.value })
                        }
                        required
                      />
                    </div>

                    {/* Type */}
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">
                        Type *
                      </label>
                      <select
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                        value={officerForm.type}
                        onChange={(e) =>
                          setOfficerForm({ ...officerForm, type: e.target.value })
                        }
                        required
                      >
                        <option value="freelancer">Freelancer</option>
                        <option value="fulltime">Full Time</option>
                      </select>
                    </div>

                    {/* Mobile */}
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">
                        Mobile Number *
                      </label>
                      <input
                        type="tel"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                        value={officerForm.mobileNumber}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '');
                          if (value.length <= 10) {
                            setOfficerForm({
                              ...officerForm,
                              mobileNumber: value,
                            });
                          }
                        }}
                        placeholder="10-digit mobile number"
                        pattern="[0-9]{10}"
                        maxLength="10"
                        required
                      />
                    </div>

                    {/* Employee ID */}
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">
                        Employee ID
                      </label>
                      <input
                        type="text"
                        value="Auto-generated"
                        disabled
                        className="w-full px-4 py-2.5 bg-gray-100 border border-gray-200 rounded-lg text-gray-500"
                      />
                    </div>

                    {/* Address */}
                    <div className="md:col-span-2">
                      <label className="text-sm font-medium text-gray-700 mb-1 block">
                        Address
                      </label>
                      <input
                        type="text"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                        value={officerForm.address}
                        onChange={(e) =>
                          setOfficerForm({
                            ...officerForm,
                            address: e.target.value,
                          })
                        }
                      />
                    </div>

                    {/* City */}
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">
                        City
                      </label>
                      <input
                        type="text"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                        value={officerForm.city}
                        onChange={(e) =>
                          setOfficerForm({
                            ...officerForm,
                            city: e.target.value,
                          })
                        }
                      />
                    </div>

                    {/* State */}
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">
                        State
                      </label>
                      <input
                        type="text"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                        value={officerForm.state}
                        onChange={(e) =>
                          setOfficerForm({
                            ...officerForm,
                            state: e.target.value,
                          })
                        }
                      />
                    </div>

                    {/* Password */}
                    <div className="md:col-span-2">
                      <label className="text-sm font-medium text-gray-700 mb-1 block">
                        Password{" "}
                        {selectedOfficer
                          ? "(Leave blank to keep current)"
                          : "*"}
                      </label>

                      <div className="relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          className="w-full px-4 py-2.5 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                          value={officerForm.password}
                          onChange={(e) =>
                            setOfficerForm({
                              ...officerForm,
                              password: e.target.value,
                            })
                          }
                          required={!selectedOfficer}
                        />

                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-purple-600"
                        >
                          {showPassword ? (
                            <FiEyeOff className="w-5 h-5" />
                          ) : (
                            <FiEye className="w-5 h-5" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* ================= PERSON FORM ================= */
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

                    {/* Name */}
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">
                        Name *
                      </label>
                      <input
                        type="text"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                        value={personForm.name}
                        onChange={(e) =>
                          setPersonForm({ ...personForm, name: e.target.value })
                        }
                        required
                      />
                    </div>

                    {/* Type */}
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">
                        Type *
                      </label>
                      <select
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                        value={personForm.type}
                        onChange={(e) =>
                          setPersonForm({ ...personForm, type: e.target.value })
                        }
                        required
                      >
                        <option value="freelancer">Freelancer</option>
                        <option value="permanent">Permanent</option>
                      </select>
                    </div>

                    {/* Mobile */}
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">
                        Mobile Number *
                      </label>
                      <input
                        type="tel"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                        value={personForm.mobileNumber}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '');
                          if (value.length <= 10) {
                            setPersonForm({
                              ...personForm,
                              mobileNumber: value,
                            });
                          }
                        }}
                        placeholder="10-digit mobile number"
                        pattern="[0-9]{10}"
                        maxLength="10"
                        required
                      />
                    </div>

                    {/* Sales Officer */}
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">
                        Sales Officer *
                      </label>
                      <select
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                        value={personForm.salesOfficer}
                        onChange={(e) =>
                          setPersonForm({
                            ...personForm,
                            salesOfficer: e.target.value,
                          })
                        }
                        required
                      >
                        <option value="">Select Officer</option>
                        {salesOfficers.map((officer) => (
                          <option key={officer._id} value={officer._id}>
                            {officer.name} ({officer.employeeId})
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Address */}
                    <div className="md:col-span-2">
                      <input
                        type="text"
                        placeholder="Address"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                        value={personForm.address}
                        onChange={(e) =>
                          setPersonForm({
                            ...personForm,
                            address: e.target.value,
                          })
                        }
                      />
                    </div>

                    {/* Password */}
                    <div className="md:col-span-2 relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        placeholder="Password"
                        className="w-full px-4 py-2.5 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                        value={personForm.password}
                        onChange={(e) =>
                          setPersonForm({
                            ...personForm,
                            password: e.target.value,
                          })
                        }
                        required={!selectedPerson}
                      />

                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2"
                      >
                        {showPassword ? <FiEyeOff /> : <FiEye />}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t bg-white flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-5 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="px-6 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
                >
                  {activeTab === "officers"
                    ? selectedOfficer
                      ? "Update Officer"
                      : "Create Officer"
                    : selectedPerson
                    ? "Update Person"
                    : "Create Person"}
                  <FiPlus className="w-4 h-4" />
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default TeamManagement;
